import React, { useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import axios from 'axios';
import { auth } from '../../../firebase';
import { 
  History, 
  ArrowDownLeft, 
  ArrowUpRight, 
  RefreshCw, 
  Search, 
  Download, 
  ExternalLink, 
  CheckCircle2, 
  Clock, 
  AlertCircle,
  TrendingUp,
  Lock,
  Unlock,
  Gift,
  Shield
} from 'lucide-react';
import "./transaction.css"

import { API_BASE } from "../../../config/api";

const Transactions = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loadingLedger, setLoadingLedger] = useState(true);
  const [activeFilter, setActiveFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [transactionLedger, setTransactionLedger] = useState([]);
  const [securityWarning, setSecurityWarning] = useState('');

  // ==========================================================================
  // HELPER: Get JWT token from localStorage
  // ==========================================================================
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token') || localStorage.getItem('eraX_auth_token');
    if (!token) {
      throw new Error('No authentication token found');
    }
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  // ==========================================================================
  // REAL-TIME AUTHENTICATION TRACKER & AUDIT LOG PIPELINE
  // ==========================================================================
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser({
          uid: user.uid,
          email: user.email
        });

        await fetchAllTransactions(user.email);
      } else {
        setCurrentUser(null);
        setLoadingLedger(false);
      }
    });
    return () => unsubscribeAuth();
  }, []);

  // ✅ Fetch all transaction types with JWT authentication & user validation
  const fetchAllTransactions = async (userEmail) => {
    try {
      setLoadingLedger(true);
      setSecurityWarning('');
      const headers = getAuthHeaders();

      console.log('📊 Fetching transaction history for user:', userEmail);

      // Fetch deposits, withdrawals, and investments in parallel
      const [depositsRes, withdrawalsRes, investmentsRes] = await Promise.all([
        axios.get(`${API_BASE}/api/deposit/history`, { headers }).catch(() => ({ data: { deposits: [] } })),
        axios.get(`${API_BASE}/api/withdrawal/history`, { headers }).catch(() => ({ data: { withdrawals: [] } })),
        axios.get(`${API_BASE}/api/investment/user-investments`, { headers }).catch(() => ({ data: { investments: [] } }))
      ]);

      // ✅ SECURITY CHECK: Verify all responses belong to the current user
      const userIds = new Set();
      
      if (depositsRes.data.userId) userIds.add(depositsRes.data.userId);
      if (withdrawalsRes.data.userId) userIds.add(withdrawalsRes.data.userId);
      if (investmentsRes.data.userId) userIds.add(investmentsRes.data.userId);

      // If we got multiple different user IDs, something is wrong
      if (userIds.size > 1) {
        console.error('❌ SECURITY ALERT: Transactions from multiple users detected!');
        setSecurityWarning('Security alert: Transaction data mismatch detected. Please refresh.');
        setTransactionLedger([]);
        setLoadingLedger(false);
        return;
      }

      const allTransactions = [];

      // ✅ Process Deposits (only for current user)
      if (depositsRes.data.success && depositsRes.data.deposits) {
        depositsRes.data.deposits.forEach(dep => {
          allTransactions.push({
            id: dep.transactionId || dep._id,
            type: 'Deposit',
            asset: dep.currency || 'USDT',
            pool: 'Capital Inbound Vault',
            amount: `+$${dep.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
            date: new Date(dep.completedAt || dep.requestedAt).toLocaleDateString('en-US', { 
              year: 'numeric', month: 'short', day: 'numeric' 
            }),
            time: new Date(dep.completedAt || dep.requestedAt).toLocaleTimeString('en-US', { 
              hour: '2-digit', minute: '2-digit', hour12: false 
            }),
            status: dep.status === 'completed' ? 'Completed' : 'Processing',
            hash: dep.transactionId || 'Pending',
            timestamp: new Date(dep.completedAt || dep.requestedAt),
            userEmail: userEmail // ✅ Track which user this belongs to
          });
        });
      }

      // ✅ Process Withdrawals (only for current user)
      if (withdrawalsRes.data.success && withdrawalsRes.data.withdrawals) {
        withdrawalsRes.data.withdrawals.forEach(wd => {
          allTransactions.push({
            id: wd.transactionId || wd._id,
            type: 'Withdrawal',
            asset: wd.cryptocurrency || 'USDT',
            pool: 'External Wallet Transfer',
            amount: `-$${wd.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
            date: new Date(wd.completedAt || wd.requestedAt).toLocaleDateString('en-US', { 
              year: 'numeric', month: 'short', day: 'numeric' 
            }),
            time: new Date(wd.completedAt || wd.requestedAt).toLocaleTimeString('en-US', { 
              hour: '2-digit', minute: '2-digit', hour12: false 
            }),
            status: wd.status === 'completed' ? 'Completed' : 
                    wd.status === 'pending' ? 'Processing' : 'Failed',
            hash: wd.transactionId || 'Pending',
            timestamp: new Date(wd.completedAt || wd.requestedAt),
            userEmail: userEmail // ✅ Track which user this belongs to
          });
        });
      }

      // ✅ Process Investment Cycles (only for current user)
      if (investmentsRes.data.success && investmentsRes.data.investments) {
        investmentsRes.data.investments.forEach(inv => {
          // Add profit payout transaction
          if (inv.interestStatus === 'claimed' || inv.status === 'auto_renewed') {
            const profitAmount = inv.profitPaidOut || inv.interestAmount;
            
            allTransactions.push({
              id: `PROFIT-${inv.transactionId}`,
              type: 'Profit Payout',
              asset: inv.symbol || 'USDT',
              pool: `Investment Cycle ${inv.cycleNumber || 1}`,
              amount: `+$${profitAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
              date: new Date(inv.actualEndDate).toLocaleDateString('en-US', { 
                year: 'numeric', month: 'short', day: 'numeric' 
              }),
              time: new Date(inv.actualEndDate).toLocaleTimeString('en-US', { 
                hour: '2-digit', minute: '2-digit', hour12: false 
              }),
              status: 'Completed',
              hash: inv.transactionId,
              timestamp: new Date(inv.actualEndDate),
              cycleNumber: inv.cycleNumber,
              isProfit: true,
              userEmail: userEmail // ✅ Track which user this belongs to
            });

            // Add auto-renewal transaction
            if (inv.status === 'auto_renewed') {
              allTransactions.push({
                id: `RENEW-${inv.transactionId}`,
                type: 'Auto-Renewal',
                asset: inv.symbol || 'USDT',
                pool: `Cycle ${inv.cycleNumber || 1} → Cycle ${(inv.cycleNumber || 1) + 1}`,
                amount: `$${inv.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
                date: new Date(inv.actualEndDate).toLocaleDateString('en-US', { 
                  year: 'numeric', month: 'short', day: 'numeric' 
                }),
                time: new Date(inv.actualEndDate).toLocaleTimeString('en-US', { 
                  hour: '2-digit', minute: '2-digit', hour12: false 
                }),
                status: 'Completed',
                hash: inv.transactionId,
                timestamp: new Date(inv.actualEndDate),
                cycleNumber: inv.cycleNumber,
                isRenewal: true,
                userEmail: userEmail // ✅ Track which user this belongs to
              });
            }
          }

          // Add initial investment transaction
          allTransactions.push({
            id: `INV-${inv.transactionId}`,
            type: 'Investment',
            asset: inv.symbol || 'USDT',
            pool: `Locked Principal (Cycle ${inv.cycleNumber || 1})`,
            amount: `-$${inv.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
            date: new Date(inv.investedAt).toLocaleDateString('en-US', { 
              year: 'numeric', month: 'short', day: 'numeric' 
            }),
            time: new Date(inv.investedAt).toLocaleTimeString('en-US', { 
              hour: '2-digit', minute: '2-digit', hour12: false 
            }),
            status: inv.status === 'active' ? 'Active' : 'Completed',
            hash: inv.transactionId,
            timestamp: new Date(inv.investedAt),
            cycleNumber: inv.cycleNumber,
            isInvestment: true,
            userEmail: userEmail // ✅ Track which user this belongs to
          });
        });
      }

      // ✅ FINAL SECURITY CHECK: Verify all transactions belong to current user
      const invalidTransactions = allTransactions.filter(tx => tx.userEmail !== userEmail);
      
      if (invalidTransactions.length > 0) {
        console.error(`❌ SECURITY ALERT: ${invalidTransactions.length} transactions don't belong to current user!`);
        setSecurityWarning(`Security alert: ${invalidTransactions.length} unauthorized transactions detected.`);
        // Filter out invalid transactions
        const validTransactions = allTransactions.filter(tx => tx.userEmail === userEmail);
        validTransactions.sort((a, b) => b.timestamp - a.timestamp);
        setTransactionLedger(validTransactions);
      } else {
        // Sort by timestamp (newest first)
        allTransactions.sort((a, b) => b.timestamp - a.timestamp);
        setTransactionLedger(allTransactions);
        console.log(`✅ Loaded ${allTransactions.length} transactions for user: ${userEmail}`);
      }

    } catch (err) {
      console.error("❌ Audit ledger failed:", err);
      
      if (err.response?.status === 401) {
        console.error('❌ Session expired. Redirecting to login...');
        localStorage.clear();
        setTimeout(() => { window.location.href = '/login'; }, 2000);
      }
      
      setTransactionLedger([]);
    } finally {
      setLoadingLedger(false);
    }
  };

  // Native System Pop-Out Share Drawer Action Trigger
  const handleShareStatement = async () => {
    const shareData = {
      title: 'EraX Capital Statement',
      text: 'Reviewing my cryptographically verified EraX account audit ledger statement.',
      url: window.location.href
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(`${shareData.text} Ledger Target: ${shareData.url}`);
        alert('Statement link copied to secure clipboard.');
      }
    } catch (err) {
      console.log('Share action dismissed by user:', err);
    }
  };

  // Processing Filter Rules
  const filteredLedger = transactionLedger.filter(tx => {
    const matchesFilter = activeFilter === 'All' || tx.type === activeFilter;
    const matchesSearch = tx.id.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          tx.asset.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          tx.pool.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  // Get icon for transaction type
  const getTransactionIcon = (tx) => {
    if (tx.isProfit) return <TrendingUp size={18} style={{ color: '#10b981' }} />;
    if (tx.isRenewal) return <RefreshCw size={16} style={{ color: '#3b82f6' }} />;
    if (tx.isInvestment) return <Lock size={16} style={{ color: '#f59e0b' }} />;
    
    switch (tx.type) {
      case 'Deposit': return <ArrowDownLeft size={18} />;
      case 'Withdrawal': return <ArrowUpRight size={18} />;
      default: return <RefreshCw size={16} />;
    }
  };

  // Get icon box color
  const getIconBoxClass = (tx) => {
    if (tx.isProfit) return 'tx-credit-tint';
    if (tx.isRenewal) return 'stocks-tint';
    if (tx.isInvestment) return 'bonds-tint';
    
    if (tx.type === 'Deposit') return 'tx-credit-tint';
    if (tx.type === 'Withdrawal') return 'tx-debit-tint';
    return 'stocks-tint';
  };

  if (loadingLedger) {
    return (
      <div className="erax-dashboard-wrapper flex items-center justify-center" style={{ minHeight: '80vh', color: '#94A3B8' }}>
        <div className="text-center">
          <p className="font-mono text-sm animate-pulse text-[#f3ba2f]">QUERYING CRYPTOGRAPHIC LEDGER HISTORIES...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="erax-dashboard-wrapper">
      
      {/* 1. TOP PREMIUM HORIZONTAL METRICS BAR */}
      <div className="erax-stats-strip wallet-hero-grid">
        <div className="erax-stat-node wallet-main-card history-hero-card">
          <div className="wallet-card-header">
            <span className="erax-node-label"><History size={14} /> Account Audit Ledger</span>
            <button className="export-ledger-btn" onClick={handleShareStatement} style={{ cursor: 'pointer' }}>
              <Download size={14} /> Share Statement
            </button>
          </div>
          <div className="wallet-balance-hero">
            <h2>Transaction Records</h2>
            <span className="currency-tag">Cryptographically Verified</span>
          </div>
        </div>
      </div>

      {/* ✅ SECURITY WARNING */}
      {securityWarning && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '2px solid #ef4444',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <Shield size={24} style={{ color: '#ef4444' }} />
          <div>
            <strong style={{ color: '#ef4444' }}>Security Alert</strong>
            <p style={{ color: '#94a3b8', margin: '4px 0 0 0', fontSize: '13px' }}>
              {securityWarning}
            </p>
          </div>
        </div>
      )}

      {/* 2. LIVE SEARCH AND FILTER MATRIX TRACK */}
      <div className="erax-ledger-control-bar">
        <div className="ledger-filter-tabs">
          {['All', 'Deposit', 'Withdrawal', 'Investment', 'Profit Payout', 'Auto-Renewal'].map((tab) => (
            <button
              key={tab}
              type="button"
              className={`filter-tab-btn ${activeFilter === tab ? 'active-tab' : ''}`}
              onClick={() => setActiveFilter(tab)}
            >
              {tab === 'All' ? 'All Logs' : tab}
            </button>
          ))}
        </div>

        <div className="ledger-search-box">
          <Search size={16} className="search-icon-inside" />
          <input 
            type="text" 
            placeholder="Search Reference, Asset or Origin Pool..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* 3. CORE INTERACTIVE HISTORICAL DATA GRID */}
      <div className="erax-workspace-panel full-width-ledger history-table-panel">
        <div className="holdings-table-container">
          
          {filteredLedger.length === 0 ? (
            <div className="empty-ledger-fallback" style={{ textAlign: 'center', padding: '60px 20px', color: '#475569' }}>
              <AlertCircle size={32} style={{ color: '#475569', margin: '0 auto 12px' }} />
              <p style={{ fontSize: '0.9rem', fontFamily: 'monospace' }}>No secure settlement records linked to this account verification parameters.</p>
            </div>
          ) : (
            filteredLedger.map((tx) => (
              <div key={tx.id} className="holding-row-card history-row-interaction">
                
                {/* Transaction Action Type Icons */}
                <div className={`holding-icon-box ${getIconBoxClass(tx)}`}>
                  {getTransactionIcon(tx)}
                </div>

                {/* Identity Cell Structure */}
                <div className="holding-identity-cell">
                  <div className="history-type-header-row">
                    <h4>
                      {tx.type}
                      {tx.cycleNumber && (
                        <span style={{ 
                          marginLeft: '8px', 
                          fontSize: '11px', 
                          background: 'rgba(59, 130, 246, 0.2)', 
                          color: '#60a5fa',
                          padding: '2px 8px',
                          borderRadius: '4px'
                        }}>
                          Cycle {tx.cycleNumber}
                        </span>
                      )}
                    </h4>
                    <span className="history-mini-timestamp">{tx.date} • {tx.time}</span>
                  </div>
                  <span className="history-pool-subtitle">Origin Gateway: <strong>{tx.pool}</strong></span>
                </div>

                {/* Tracking Code/Hash Block Nodes */}
                <div className="holding-data-cell hide-mobile">
                  <span className="cell-top font-mono-label">Ledger Ref</span>
                  <span className="cell-bottom text-white hash-link-node">
                    {tx.hash.substring(0, 12)}... <ExternalLink size={10} className="text-gold" />
                  </span>
                </div>

                {/* Financial Ledger Balance Settlement State */}
                <div className="holding-data-cell text-right">
                  <span className={`cell-top market-value ${
                    tx.amount.startsWith('+') ? 'text-green' : 
                    tx.amount.startsWith('-') ? 'text-red' : 'text-white'
                  }`}>
                    {tx.amount}
                  </span>
                  
                  <span className={`cell-bottom tx-status-indicator ${
                    tx.status === 'Completed' || tx.status === 'Active' ? 'status-green' : 
                    tx.status === 'Processing' ? 'status-gold' : 'status-red'
                  }`}>
                    {tx.status === 'Completed' && <CheckCircle2 size={12} />}
                    {tx.status === 'Active' && <CheckCircle2 size={12} />}
                    {tx.status === 'Processing' && <Clock size={12} />}
                    {tx.status === 'Failed' && <AlertCircle size={12} />}
                    {tx.status}
                  </span>
                </div>

              </div>
            ))
          )}

        </div>
      </div>

    </div>
  );
};

export default Transactions;